import { z } from 'zod';
import { PartnerChannelSchema, type PartnerChannel } from '../enums/partner-channel.js';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';
import { MATRICULE_PREFIX_REGEX } from './matricule.js';

/**
 * Rôles que le RP/admin peut placer dans `Partner.invitableRoles`. On
 * **exclut admin** (toujours autorisé implicitement, et ne dépend pas du
 * partner) et **vendeur** (n'a aucune autorité hiérarchique pour inviter).
 * Le responsable back-office y figure par défaut : créer les comptes est sa
 * raison d'être — mais un RP peut le lui retirer (« certains me diront non »).
 */
export const InvitableRoleSchema = z.enum([
  'responsable_partenaire',
  'responsable_backoffice',
  'manager',
  'superviseur',
]);
export type InvitableRole = z.infer<typeof InvitableRoleSchema>;

/**
 * Rôles invitants au départ, par canal. Porte-à-porte : RP et manager (le
 * comportement historique). Magasin : le superviseur aussi — c'est lui qui
 * crée ses vendeurs au quotidien. La liste reste modifiable par le RP ; la
 * typologie ne fait que poser la valeur de départ.
 */
export const DEFAULT_INVITABLE_ROLES_BY_CHANNEL: Record<PartnerChannel, InvitableRole[]> = {
  door_to_door: ['responsable_partenaire', 'responsable_backoffice', 'manager'],
  retail: ['responsable_partenaire', 'responsable_backoffice', 'manager', 'superviseur'],
};

/**
 * Rôles invitants après un changement de canal : passer en magasin **ajoute**
 * le superviseur à ce que le RP avait choisi — sans lui rendre un rôle qu'il
 * aurait retiré ; le sens inverse ne retire rien, la liste lui appartient.
 */
export function invitableRolesAfterChannelChange(
  current: InvitableRole[],
  from: PartnerChannel,
  to: PartnerChannel,
): InvitableRole[] {
  if (from === to || to !== 'retail') return current;
  return current.includes('superviseur') ? current : [...current, 'superviseur'];
}

export const PartnerSchema = z.object({
  id: UuidV7Schema,
  name: z.string().min(1).max(200),
  description: z.string().max(1000).nullable().optional(),
  /**
   * Préfixe 3 chars [A-Z0-9] utilisé pour générer les matricules des users
   * du partenaire (`XXX-NNNNNN-Y`). Choisi à la création, unique sur tout
   * le système, immuable après création (les matricules existants en
   * dépendent).
   */
  matriculePrefix: z.string().regex(MATRICULE_PREFIX_REGEX),
  archived: z.boolean(),
  /** Single verticale this partner is engaged on (nullable). */
  engagedVerticaleId: UuidV7Schema.nullable().optional(),
  /**
   * Verticales this partner authorizes prospecting on. Empty array = all
   * active verticales (rétrocompat).
   */
  allowedVerticaleIds: z.array(UuidV7Schema).default([]),
  /**
   * Rôles autorisés à inviter / créer des utilisateurs au sein de ce
   * partenaire. `admin` est toujours autorisé en plus de cette liste.
   */
  invitableRoles: z
    .array(InvitableRoleSchema)
    .default(['responsable_partenaire', 'responsable_backoffice', 'manager']),
  /** Canal du réseau — d'où découlent tablettes partagées, PIN, classement (cf. typologie-partenaire.md). */
  channel: PartnerChannelSchema.default('door_to_door'),
  /** Boutons « Appeler » / « SMS » vers le prospect, sur l'appareil de l'utilisateur. */
  contactButtons: z.boolean().default(true),
  /** Champ « Numéro de commande » du questionnaire Telco, transmis au Lab. Réservé admin. */
  telcoOrderNumber: z.boolean().default(false),
  /**
   * Ce partner agrège d'autres réseaux (cf. `Partner.isAggregator`). Le RP de
   * ce partner voit et filtre tout le descendant. Réservé admin.
   */
  isAggregator: z.boolean().default(false),
  /** Agrégateur auquel ce réseau est rattaché. Null = réseau autonome. */
  aggregatorPartnerId: UuidV7Schema.nullable().optional(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
});
export type Partner = z.infer<typeof PartnerSchema>;

export const CreatePartnerSchema = z.object({
  name: z.string().min(1).max(200),
  matriculePrefix: z.string().regex(MATRICULE_PREFIX_REGEX, '3 caractères A-Z ou 0-9 requis'),
  description: z.string().max(1000).optional(),
  engagedVerticaleId: UuidV7Schema.nullable().optional(),
  allowedVerticaleIds: z.array(UuidV7Schema).default([]),
  invitableRoles: z.array(InvitableRoleSchema).optional(),
  channel: PartnerChannelSchema.optional(),
  contactButtons: z.boolean().optional(),
  /** Admin-only — le service rejette un non-admin qui l'envoie. */
  telcoOrderNumber: z.boolean().optional(),
  /**
   * Les deux champs d'agrégation sont **admin-only** — le service rejette un
   * non-admin qui les envoie (cf. `PartnersService.assertAggregationRights`).
   * Ils vivent ici pour que l'admin puisse rattacher un réseau existant à un
   * agrégateur, ce qui est exactement le rattrapage a posteriori du fallback
   * manuel (Cyrille crée les RP, on les rebalance ensuite).
   */
  isAggregator: z.boolean().optional(),
  aggregatorPartnerId: UuidV7Schema.nullable().optional(),
});
export type CreatePartnerInput = z.infer<typeof CreatePartnerSchema>;

// `matriculePrefix` est volontairement absent ici : immuable après création.
export const UpdatePartnerSchema = CreatePartnerSchema.omit({ matriculePrefix: true })
  .partial()
  .extend({
    archived: z.boolean().optional(),
  });
export type UpdatePartnerInput = z.infer<typeof UpdatePartnerSchema>;
